import React, { useState, useEffect, useCallback } from "react";
import api from "../services/api";
import "./DemandeList.css";

interface MariageInfo {
  bride: string;
  groom: string;
  date: string;
}

interface InviteInfo {
  nom: string;
  table: string;
}

interface TraitePar {
  nom: string;
  role: string;
}

interface Demande {
  _id: string;
  type: string;
  raison: string;
  statut: "en_attente" | "acceptee" | "refusee";
  createdAt: string;
  dateTraitement?: string;
  commentaireMegaadmin?: string;
  traitePar?: TraitePar;
  mariageId?: MariageInfo;
  inviteId?: InviteInfo;
  details?: any;
}

interface ApiResponse {
  success: boolean;
  demandes: Demande[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

interface DeleteResponse {
  success: boolean;
  message: string;
}

const DemandeList: React.FC = () => {
  const [demandes, setDemandes] = useState<Demande[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("tous");

  const fetchDemandes = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filter !== "tous") params.append("statut", filter);
      
      const response = await api.get<ApiResponse>(`/demandes/mes-demandes?${params.toString()}`);
      if (response.data.success) {
        setDemandes(response.data.demandes);
      }
    } catch (error) {
      console.error("Erreur chargement demandes:", error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchDemandes();
  }, [fetchDemandes]);

  const handleDeleteDemande = async (demandeId: string) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette demande ?")) {
      return;
    }

    try {
      const response = await api.delete<DeleteResponse>(`/demandes/mes-demandes/${demandeId}`);
      
      if (response.data.success) {
        alert("Demande supprimée avec succès");
        fetchDemandes();
      } else {
        alert(response.data.message || "Erreur lors de la suppression");
      }
    } catch (error: any) {
      console.error("Erreur suppression demande:", error);
      alert(error.response?.data?.message || "Erreur lors de la suppression");
    }
  };

  const getTypeLabel = (type: string): string => {
    const types: Record<string, string> = {
      "suppression_mariage": "Suppression mariage",
      "suppression_tous_invites": "Suppression tous les invités",
      "suppression_invite": "Suppression invité"
    };
    return types[type] || type;
  };

  const getStatutBadge = (statut: string): string => {
    const classes: Record<string, string> = {
      "en_attente": "badge-warning",
      "acceptee": "badge-success",
      "refusee": "badge-danger"
    };
    return `statut-badge ${classes[statut] || ""}`;
  };

  const getStatutLabel = (statut: string): string => {
    const labels: Record<string, string> = {
      "en_attente": "En attente",
      "acceptee": "Acceptée",
      "refusee": "Refusée"
    };
    return labels[statut] || statut;
  };

  const formatDate = (date: string): string => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="loading">Chargement des demandes...</div>;
  }

  return (
    <div className="demande-list-container">
      <div className="filters">
        <label>Filtrer par statut :</label>
        <select value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="tous">Toutes les demandes</option>
          <option value="en_attente">En attente</option>
          <option value="acceptee">Acceptées</option>
          <option value="refusee">Refusées</option>
        </select>
      </div>

      {demandes.length === 0 ? (
        <p className="empty-message">Aucune demande trouvée</p>
      ) : (
        <div className="demandes-grid">
          {demandes.map((demande) => (
            <div key={demande._id} className="demande-card">
              <div className="demande-header">
                <h3>{getTypeLabel(demande.type)}</h3>
                <div className="header-actions">
                  <span className={getStatutBadge(demande.statut)}>
                    {getStatutLabel(demande.statut)}
                  </span>
                  
                  <button
                    className="delete-demande-btn"
                    onClick={() => handleDeleteDemande(demande._id)}
                    title="Supprimer la demande"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor"/>
                    </svg>
                  </button>
                </div>
              </div>

              <div className="demande-body">
                <p className="raison">
                  <strong>Raison :</strong> {demande.raison}
                </p>

                {demande.mariageId && (
                  <p className="mariage-info">
                    <strong>Mariage :</strong> {demande.mariageId.bride} & {demande.mariageId.groom} - {demande.mariageId.date}
                  </p>
                )}

                {demande.inviteId && (
                  <p className="invite-info">
                    <strong>Invité :</strong> {demande.inviteId.nom} (Table {demande.inviteId.table})
                  </p>
                )}

                <p className="date">
                  <strong>Demande créée le :</strong> {formatDate(demande.createdAt)}
                </p>

                {demande.statut !== "en_attente" && (
                  <div className="traitement-info">
                    <p className="date-traitement">
                      <strong>Traitée le :</strong> {demande.dateTraitement ? formatDate(demande.dateTraitement) : "Date non disponible"}
                    </p>
                    
                    {demande.traitePar && (
                      <p className="traite-par">
                        <strong>Par :</strong> {demande.traitePar.nom} ({demande.traitePar.role})
                      </p>
                    )}
                    
                    {demande.commentaireMegaadmin && (
                      <div className="commentaire">
                        <strong>Commentaire :</strong>
                        <p className="commentaire-texte">{demande.commentaireMegaadmin}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DemandeList;